import { motion } from "motion/react";
import * as Flags from "country-flag-icons/react/3x2";

interface CountryFlagProps {
  code: string;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  circle?: boolean;
  className?: string;
  animate?: boolean;
}

const sizeMap = {
  sm: { flag: "h-5 w-7", wrapper: "h-5 w-5" },
  md: { flag: "h-6 w-9", wrapper: "h-8 w-8" },
  lg: { flag: "h-8 w-12", wrapper: "h-12 w-12" },
  xl: { flag: "h-11 w-16", wrapper: "h-16 w-16" },
  hero: { flag: "h-16 w-24", wrapper: "h-24 w-24" },
};

/**
 * Renders a real SVG country flag from country-flag-icons — NO emojis, no raster images.
 * Supports circle or rectangular rendering with optional entrance animation.
 */
export function CountryFlag({
  code,
  size = "md",
  circle = true,
  className = "",
  animate = true,
}: CountryFlagProps) {
  const upperCode = code.toUpperCase() as keyof typeof Flags;
  const FlagComponent = Flags[upperCode] as React.ComponentType<any> | undefined;

  if (!FlagComponent) {
    // Fallback for unknown codes
    return (
      <div
        className={`${circle ? sizeMap[size].wrapper : sizeMap[size].flag} ${
          circle ? "rounded-full" : "rounded-[2px]"
        } bg-[var(--color-bg-tertiary)] flex items-center justify-center shadow-sm ${className}`}
      >
        <span className="text-[8px] text-[var(--color-text-tertiary)]">{code}</span>
      </div>
    );
  }

  const flagElement = circle ? (
    <div
      className={`${sizeMap[size].wrapper} rounded-full overflow-hidden shadow-sm flex-shrink-0 ${className}`}
    >
      <FlagComponent
        className="h-full w-full object-cover scale-[1.35]"
        style={{ objectFit: "cover" }}
      />
    </div>
  ) : (
    <FlagComponent
      className={`${sizeMap[size].flag} flex-shrink-0 rounded-[2px] shadow-sm ${className}`}
    />
  );

  if (!animate) return flagElement;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
      className="inline-flex flex-shrink-0"
    >
      {flagElement}
    </motion.div>
  );
}

/**
 * A prominent flag badge — used in hero sections and section headers
 * Shows the flag with a subtle glow ring in the country's accent color
 */
export function CountryFlagBadge({
  code,
  accentColor,
  size = "xl",
  label,
}: {
  code: string;
  accentColor?: string;
  size?: "lg" | "xl" | "hero";
  label?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", duration: 0.6, bounce: 0.25 }}
      className="flex flex-col items-center gap-2"
    >
      <div
        className="rounded-full p-1"
        style={{
          background: accentColor
            ? `linear-gradient(135deg, ${accentColor}40, ${accentColor}15)`
            : undefined,
          boxShadow: accentColor
            ? `0 0 30px ${accentColor}20, 0 0 60px ${accentColor}08`
            : undefined,
        }}
      >
        <CountryFlag code={code} size={size} circle animate={false} />
      </div>
      {label && (
        <motion.span
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-xs tracking-widest uppercase"
          style={{ color: accentColor || "var(--color-text-tertiary)" }}
        >
          {label}
        </motion.span>
      )}
    </motion.div>
  );
}